// ============================================================
// Commandery Access Control — data model + gated create adapter.
//
// Square Code is the OPERATOR control panel. Olivium / Secure Share is the
// ACCESS AUTHORITY (invite creation, one-time codes, redemption, sessions live
// there). This module holds the option vocabularies + permission matrix the
// operator UI renders, and a GATED create-access adapter that NEVER performs a
// real invite write from the browser and NEVER touches a service-role key.
//
// Honest by construction: real access creation requires the Olivium Secure Share
// backend route to be wired + approved. Until then the adapter returns a
// clearly-gated result and the UI shows "Backend connection required".
// See docs/client-connect/COMMANDERY_ACCESS_CONTROL_BACKEND_INTEGRATION.md
// ============================================================

import { api, ApiError, isAuthError, isForbiddenError } from '../../api/client'

// ── Option vocabularies (operator Create Access panel) ──

export const ACCESS_TYPES = [
  { key: 'workspace', label: 'Workspace' },
  { key: 'file', label: 'File' },
  { key: 'deliverable', label: 'Deliverable' },
  { key: 'call', label: 'Call' },
  { key: 'approval', label: 'Approval' },
  { key: 'portal_session', label: 'Portal session' },
] as const
export type AccessType = (typeof ACCESS_TYPES)[number]['key']

export const ROLES = [
  { key: 'viewer', label: 'Viewer' },
  { key: 'client_owner', label: 'Client owner' },
  { key: 'client_collaborator', label: 'Client collaborator' },
  { key: 'operator', label: 'Operator' },
] as const
export type Role = (typeof ROLES)[number]['key']

export const VISIBILITY = [
  { key: 'client_visible', label: 'Client visible' },
  { key: 'restricted', label: 'Restricted' },
  { key: 'internal_only', label: 'Internal only' },
] as const
export type Visibility = (typeof VISIBILITY)[number]['key']

export const EXPIRATION_OPTIONS = [
  { key: '15m', label: '15 minutes', minutes: 15 },
  { key: '1h', label: '1 hour', minutes: 60 },
  { key: '24h', label: '24 hours', minutes: 1440 },
  { key: '7d', label: '7 days', minutes: 10080 },
  { key: 'custom', label: 'Custom', minutes: null },
] as const
export type Expiration = (typeof EXPIRATION_OPTIONS)[number]['key']

// Access modes mirror the merged Secure Shield Controlled Portal Session Mode
// design (Olivium docs/olivium-secure-share/SECURE_SHIELD_CONTROLLED_PORTAL_SESSION_MODE.md).
export const ACCESS_MODES = [
  { key: 'test_url', label: 'Test URL', note: 'Short-lived, heavily monitored, copied link fails.' },
  { key: 'live_client', label: 'Live client', note: 'Project-scoped, resumable only if binding valid.' },
  { key: 'real', label: 'Real', note: 'Strict identity/session binding, full audit + alerting.' },
] as const
export type AccessMode = (typeof ACCESS_MODES)[number]['key']

// ── Invite status vocabulary ──

export const INVITE_STATUSES = [
  'draft', 'pending', 'active', 'redeemed', 'expired', 'revoked', 'failed',
] as const
export type InviteStatus = (typeof INVITE_STATUSES)[number]

// Maps an invite status onto a shell statusAccent() token key.
export function inviteStatusTone(status: InviteStatus): string {
  switch (status) {
    case 'active': return 'active'
    case 'redeemed': return 'verified'
    case 'pending': case 'draft': return 'pending'
    case 'expired': return 'archived'
    case 'revoked': case 'failed': return 'critical'
    default: return 'archived'
  }
}

// ── Olivium protection state (subtle per-row branding) ──

export const PROTECTION_STATES = {
  protected: 'Protected by Olivium',
  access_controlled: 'Access controlled',
  receipt_ready: 'Receipt-ready',
  revoked: 'Revoked',
  expired: 'Expired',
  session_bound: 'Session-bound (later)',
} as const
export type ProtectionState = keyof typeof PROTECTION_STATES

export function protectionForStatus(status: InviteStatus): ProtectionState {
  if (status === 'revoked') return 'revoked'
  if (status === 'expired') return 'expired'
  if (status === 'redeemed') return 'receipt_ready'
  if (status === 'active') return 'access_controlled'
  return 'protected'
}

// ── Permission matrix (role × resource) ──

export const RESOURCES = [
  'workspace', 'messages', 'files', 'deliverables', 'calls',
  'calendar', 'approvals', 'tasks', 'testing links', 'receipts',
] as const
export type Resource = (typeof RESOURCES)[number]

export type Grant = 'full' | 'read' | 'request' | 'none' | 'planned'

export const PERMISSION_MATRIX: Record<Role, Record<Resource, Grant>> = {
  operator: {
    workspace: 'full', messages: 'full', files: 'full', deliverables: 'full', calls: 'full',
    calendar: 'full', approvals: 'full', tasks: 'full', 'testing links': 'full', receipts: 'full',
  },
  client_owner: {
    workspace: 'full', messages: 'full', files: 'full', deliverables: 'full', calls: 'full',
    calendar: 'full', approvals: 'full', tasks: 'request', 'testing links': 'read', receipts: 'read',
  },
  client_collaborator: {
    workspace: 'read', messages: 'full', files: 'read', deliverables: 'read', calls: 'request',
    calendar: 'read', approvals: 'request', tasks: 'read', 'testing links': 'none', receipts: 'none',
  },
  viewer: {
    workspace: 'read', messages: 'read', files: 'read', deliverables: 'read', calls: 'none',
    calendar: 'read', approvals: 'none', tasks: 'none', 'testing links': 'none', receipts: 'none',
  },
}

export const GRANT_LABEL: Record<Grant, string> = {
  full: 'Full', read: 'Read', request: 'Request', none: '—', planned: 'Planned',
}

// ── Gated create-access adapter ──
//
// This is the ONLY place the operator UI would talk to the access authority.
// It is intentionally gated: it performs NO write and returns a clearly-labeled
// result so the UI can stay honest. When the Olivium Secure Share route is wired
// and approved, this becomes a server-side call (the service-role key stays in
// that server/edge environment — NEVER in this browser bundle).

export interface CreateAccessInput {
  projectId: string
  label: string
  recipient?: string
  accessType: AccessType
  role: Role
  visibility: Visibility
  expiration: Expiration
  mode: AccessMode
  notes?: string
}

export type CreateAccessResult =
  | { ok: true; dry_run: true; invite: { id: string; status: InviteStatus }; message: string } // dry-run mock
  | { // live Olivium invite (Phase 1D-c) — one-time plaintext code, never a hash
      ok: true; live: true; invite: { id: string; status: InviteStatus }
      access_code: string; code_reveal: 'one_time'; code_storage: 'kdf_hash_only'; message: string
    }
  | { ok: false; gated: true; reason: 'backend_connection_required'; message: string }
  | { ok: false; error: true; message: string } // safe, generic failure (no raw backend error)

// Clearly-labeled live mode copy (shown when Live Olivium Access is selected).
export const LIVE_MODE_LABEL =
  'Live Olivium Access — creates a real Secure Share invite and one-time access code.'

// Operator-safe copy per fail-closed server code. No env names, no raw errors.
const LIVE_ERROR_COPY: Record<string, string> = {
  live_identity_missing: 'Live Olivium access is not connected for this operator session yet.',
  live_not_configured: 'Live Olivium access is not connected in this environment yet, so no invite was created.',
  live_not_connected: 'Live Olivium access is not connected yet, so no invite was created.',
  live_mapping_missing: 'Live Olivium access is not connected for this project yet, so no invite was created.',
  live_forbidden: 'You need Olivium owner/admin authority to create live access. Nothing was created.',
  live_failed: 'Live access could not be created. Nothing was created — please try again.',
}

// Operator-safe gated message — no env names, no secrets, no raw errors.
export const BACKEND_REQUIRED_MESSAGE =
  'Access creation connects to Olivium Secure Share. That connection is not enabled in this environment yet, so no invite was created.'

export async function createAccessInvite(_input: CreateAccessInput): Promise<CreateAccessResult> {
  // GATED: no real invite write. When wired, this calls a server-side adapter
  // (POST to an approved route) that itself invokes Olivium ss-invite-create with
  // the operator's JWT; the service-role key never reaches this browser.
  return { ok: false, gated: true, reason: 'backend_connection_required', message: BACKEND_REQUIRED_MESSAGE }
}

// ── DRY-RUN adapter (Phase 1D-b) ──
//
// Calls the server dry-run route POST /api/access/create, which verifies operator
// ownership and returns a clearly-flagged MOCK ({ dry_run: true }) — NO real
// invite, code, token, hash, Olivium call, write, or email. This lets the operator
// exercise the Create Access flow end-to-end safely. Errors map to a generic,
// operator-safe message (no raw backend error, no env names).

export async function createAccessInviteDryRun(input: CreateAccessInput): Promise<CreateAccessResult> {
  try {
    const r = await api.access.create({
      project_id: input.projectId,
      label: input.label,
      recipient: input.recipient || undefined,
      access_type: input.accessType,
      role: input.role,
      visibility: input.visibility,
      expiration: input.expiration,
      mode: input.mode,
      notes: input.notes || undefined,
    })
    return {
      ok: true,
      dry_run: true,
      invite: { id: r.invite.id, status: (r.invite.status as InviteStatus) ?? 'draft' },
      message: r.message || 'Dry run only — no live access created.',
    }
  } catch (err) {
    return { ok: false, error: true, message: dryRunErrorMessage(err) }
  }
}

function dryRunErrorMessage(err: unknown): string {
  if (isAuthError(err)) return 'Sign in as the operator to run a dry run for this project.'
  if (isForbiddenError(err)) return 'You need to own this project to run a dry run for it.'
  // Generic — never a raw backend error, code, env name, or hash.
  return 'The dry run could not be completed. Check the details and try again.'
}

// ── LIVE adapter (Phase 1D-c) ──
//
// Calls POST /api/access/create with { live: true }. The server verifies operator
// ownership, resolves the Olivium project server-side, and calls Olivium
// ss-invite-create — returning the ONE-TIME plaintext access code. Down-map:
// only project_id + label are sent; the richer form fields stay operator metadata
// (not enforced by Olivium yet). Failures map to operator-safe copy — never a raw
// backend error, env name, stored hash, or a faked success.
export async function createAccessInviteLive(input: CreateAccessInput): Promise<CreateAccessResult> {
  try {
    const r = await api.access.create({ project_id: input.projectId, label: input.label, live: true })
    if (r.ok && r.live && typeof r.access_code === 'string' && r.access_code) {
      return {
        ok: true,
        live: true,
        invite: { id: r.invite.id, status: (r.invite.status as InviteStatus) ?? 'active' },
        access_code: r.access_code,
        code_reveal: 'one_time',
        code_storage: 'kdf_hash_only',
        message: r.message || 'Live access created — copy the one-time code now.',
      }
    }
    // No access code returned → never fake success.
    return { ok: false, error: true, message: LIVE_ERROR_COPY.live_failed }
  } catch (err) {
    return { ok: false, error: true, message: liveErrorMessage(err) }
  }
}

function liveErrorMessage(err: unknown): string {
  if (err instanceof ApiError && err.code && LIVE_ERROR_COPY[err.code]) return LIVE_ERROR_COPY[err.code]
  if (isAuthError(err)) return LIVE_ERROR_COPY.live_identity_missing
  if (isForbiddenError(err)) return LIVE_ERROR_COPY.live_forbidden
  // Generic — never a raw backend error, code, env name, or hash.
  return LIVE_ERROR_COPY.live_failed
}

// Clearly-flagged PREVIEW invites so the operator can see the list/reveal shape
// without any live data. Never returned as live; every row is preview-only.
export interface PreviewInvite {
  id: string
  recipient: string
  accessType: AccessType
  role: Role
  status: InviteStatus
  created: string
  expires: string
  redeemed: string | null
  mode: AccessMode
  preview: true
}

export function previewInvites(): PreviewInvite[] {
  // Static, deterministic, obviously-not-real sample rows (no Date.now()).
  return [
    { id: 'preview-1', recipient: 'client.owner@example.com', accessType: 'workspace', role: 'client_owner', status: 'active', created: '2026-06-01', expires: '2026-06-08', redeemed: '2026-06-01', mode: 'live_client', preview: true },
    { id: 'preview-2', recipient: 'tester@example.com', accessType: 'portal_session', role: 'viewer', status: 'pending', created: '2026-06-02', expires: '2026-06-02', redeemed: null, mode: 'test_url', preview: true },
    { id: 'preview-3', recipient: 'collab@example.com', accessType: 'deliverable', role: 'client_collaborator', status: 'revoked', created: '2026-05-28', expires: '2026-06-04', redeemed: null, mode: 'real', preview: true },
  ]
}
